import emptyStateTexture from '@/assets/empty-state-texture.svg';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { buildAiFrom } from '@/features/copilot/utils/build-ai-from';
import { close, Sidebar, SidebarHeader, toggle } from '@/features/sidebar';
import { useCopilotSidebar } from './hooks/use-copilot-sidebar';
import { useSupportChat } from '@/features/support';
import { useAnalytics } from '@/services/analytics';
import { AiEnabledGuard, AiNotEnabledFallback } from '@/components/guards';

import { useRouter } from '@tanstack/react-router';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { sentryCaptureException } from '@/utils/sentry';
import { useCopilotLastTurnStopped, useCopilotMessages, useCopilotStatus } from '@customerio/hydra-copilot/react';
import {
  Banner,
  Box,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTriggerButton,
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateTitle,
  Heading,
  Image,
  Text,
  Tooltip,
  Truncated,
} from '@customerio/pluma-components/react';
import { m, useReducedMotion } from 'motion/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as chatStyles from './chat.css';
import { AiGradientSparkleIcon } from './components/ai-gradient-sparkle-icon';
import { Conversation, ConversationScrollButton, type ConversationRef } from './components/conversation';

import { AgentBetaLabel } from './components/agent-beta-label';
import { useCopilotChatContext, useCopilotSessionContext, useMasqAccessLevel } from './components/session-provider';
import { MarqueeSuggestions, useSuggestions } from './components/suggestions';

import { useGreeting } from '@/features/greeting';
import {
  ENTRANCE_FADE_UP_ANIMATE,
  entranceExpressiveTransition,
  entranceFadeUpInitial,
  entranceStaggerTransition,
} from '@/utils/motion';
import { useIsAgentFullscreen } from './hooks';

import { TypeWriterText } from '@/components/feedback/typewriter-text';
import { useCopilotMeta } from '@customerio/hydra-copilot/react';

import { AnimateMessage } from './components/chat-messages/animate-message';
import { Input } from './components/chat-messages/input';
import { CyclingStatusText, RecentChats, ChatSkeleton } from './components/chat-messages/chat-status';
import { Message, getMessageKey } from './components/chat-messages/message';
import { NextMessageSuggestions } from './components/chat-messages/next-message-suggestions';
import { useCopilotSkillSlugs } from './hooks/use-copilot-skills';

/** Wraps the copilot welcome block in one entrance; keeps heading/description/actions in DOM order for a11y. */
const MotionWelcomePanel = m.create(Box);
/** Sidebar column slot that must flex-grow; plain m.div does not receive Pluma flex props. */
const MotionSidebarMessagesColumn = m.create(Box);

function CopilotActions() {
  const router = useRouter();
  const session = useCopilotSessionContext();
  const workspace = useCurrentWorkspace();
  const { track } = useAnalytics();

  if (workspace == null) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTriggerButton isIconOnly icon="more" variant="subtle" size="sm" />
      <DropdownMenuContent>
        <DropdownMenuItem
          icon="add"
          onClick={() => {
            session.reset();
            track('aiAssistant', 'newChatClicked', {});
          }}
        >
          New chat
        </DropdownMenuItem>
        <DropdownMenuItem
          icon="time-window"
          onClick={() => {
            toggle('copilot-history');
          }}
        >
          History
        </DropdownMenuItem>
        <DropdownMenuItem
          icon="sparkle"
          href={
            router.buildLocation({
              to: '/workspaces/$workspaceId/settings/ai-business-profile',
              params: { workspaceId: workspace.id },
            }).href
          }
        >
          Context
        </DropdownMenuItem>
        <DropdownMenuItem
          icon="trigger-date"
          href={
            router.buildLocation({
              to: '/workspaces/$workspaceId/agent/routines',
              params: { workspaceId: workspace.id },
            }).href
          }
        >
          Routines
        </DropdownMenuItem>
        <DropdownMenuItem
          icon="star"
          href={
            router.buildLocation({ to: '/workspaces/$workspaceId/agent/skills', params: { workspaceId: workspace.id } })
              .href
          }
        >
          Skills
        </DropdownMenuItem>
        <DropdownMenuItem icon="manage" href="/settings/ai">
          AI settings
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function CopilotName() {
  const session = useCopilotSessionContext();
  const meta = useCopilotMeta(session);
  return (
    <Truncated>
      <TypeWriterText text={meta.name ?? 'Agent'} />
    </Truncated>
  );
}

type CopilotInputProps = {
  onSubmit?: () => void;
  placeholder?: string;
  wrapperClassName?: string;
  newSessionOnSubmit?: boolean;
};

export function CopilotInput({
  onSubmit,
  placeholder,
  wrapperClassName,
  newSessionOnSubmit = false,
}: CopilotInputProps = {}) {
  const { session, input: sharedInput, setInput: setSharedInput } = useCopilotChatContext();
  const [localInput, setLocalInput] = useState('');
  const { isMasqWithoutAccess } = useMasqAccessLevel();
  const resetSession = useCallback(() => session.reset(), [session]);

  // Hide input when the masq user has no access.
  if (isMasqWithoutAccess) {
    return null;
  }

  const input = newSessionOnSubmit ? localInput : sharedInput;
  const setInput = newSessionOnSubmit ? setLocalInput : setSharedInput;

  return (
    <Input
      session={session}
      input={input}
      setInput={setInput}
      onSubmit={onSubmit}
      placeholder={placeholder}
      wrapperClassName={wrapperClassName}
      resetSession={newSessionOnSubmit ? resetSession : undefined}
    />
  );
}

export function CopilotSupportButton() {
  const supportChat = useSupportChat();

  if (!supportChat.isVisible || !supportChat.isHandoverAllowed || !supportChat.zendeskChatEnabled) {
    return null;
  }

  return (
    <Tooltip content="Chat with support" isCustom={true}>
      {(context) => (
        <Button
          variant="subtle"
          onClick={async () => {
            const success = await supportChat.openChat();
            if (success) {
              close();
            }
          }}
          isLoading={supportChat.isLoading}
          size="sm"
          isIconOnly={true}
          icon="chat-question"
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps()}
        />
      )}
    </Tooltip>
  );
}

export function CopilotMessages() {
  const session = useCopilotSessionContext();
  const messages = useCopilotMessages(session);
  const status = useCopilotStatus(session);
  const lastTurnStopped = useCopilotLastTurnStopped(session);
  const { isMasq, isMasqWithoutAccess } = useMasqAccessLevel();
  const isSending = status === 'sending';
  const isReceiving = status === 'receiving' || status === 'recovering';
  const shouldReduceMotion = useReducedMotion();
  const skillSlugs = useCopilotSkillSlugs();

  const [copilotWelcomeCycle, setCopilotWelcomeCycle] = useState(0);
  const previousSessionForWelcomeRef = useRef(session);
  // Not useMountEffect: replay welcome entrance when the session instance changes (e.g. New chat).
  useEffect(() => {
    if (previousSessionForWelcomeRef.current !== session) {
      previousSessionForWelcomeRef.current = session;
      setCopilotWelcomeCycle((n) => n + 1);
    }
  }, [session]);

  const welcomeMotionInitial = entranceFadeUpInitial(shouldReduceMotion);

  const supportChat = useSupportChat();
  const { track } = useAnalytics();
  const suggestionState = useSuggestions(session);
  const greeting = useGreeting({ withEmoji: false });
  const conversationRef = useRef<ConversationRef>(null);

  useEffect(() => {
    if (suggestionState.isVisible === true) {
      conversationRef.current?.scrollToBottom();
    }
  }, [suggestionState.isVisible]);

  const handleSuggestionClick = async (message: string, index: number) => {
    try {
      await session.sendMessage(message);
      track('aiAssistant', 'suggestionClicked', {
        Index: index,
        'Click Location': 'sidebar',
      });
    } catch (error) {
      sentryCaptureException(error);
    }
  };

  return (
    <Box flexGrow="1" h="0" display="flex" flexDirection="column">
      {isMasq ? (
        <Box px="200" pt="100" flexShrink="0">
          <Banner
            variant="warning"
            title="Masquerade session"
            description={
              <>
                {isMasqWithoutAccess
                  ? "You don't have masquerade access to this account. The agent cannot read or modify account data."
                  : 'You are masquerading into this account with read-only access. The agent can read but cannot modify account data.'}
              </>
            }
          />
        </Box>
      ) : null}
      {status === 'error' ? (
        <Box h="full" display="flex" justifyContent="center" alignItems="center" pb="800">
          <EmptyState>
            <EmptyStateTitle icon="error">Failed to load</EmptyStateTitle>
            <EmptyStateDescription>There was an error loading Agent. Please try again.</EmptyStateDescription>
            <EmptyStateActions>
              <Button
                variant="primary"
                onClick={() => {
                  session.reset();
                }}
              >
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyState>
        </Box>
      ) : (
        <>
          {status === 'initializing' ? (
            <Box flexGrow="1" display="flex" flexDirection="column" className={chatStyles.scrollContentWrapper}>
              <ChatSkeleton />
            </Box>
          ) : messages.length === 0 ? (
            <>
              <Box flexGrow="1" display="flex" flexDirection="column" position="relative" px="200">
                <Box flexGrow="1" display="flex" justifyContent="center" alignItems="center">
                  <MotionWelcomePanel
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    gap="150"
                    w="full"
                    initial={welcomeMotionInitial}
                    animate={ENTRANCE_FADE_UP_ANIMATE}
                    transition={entranceExpressiveTransition(shouldReduceMotion)}
                    key={`copilot-welcome-${copilotWelcomeCycle}`}
                  >
                    <AiGradientSparkleIcon size={40} />
                    <Heading level="1" textAlign="center">
                      {greeting.hello}
                    </Heading>
                    <Text color="subtle" textAlign="center">
                      {greeting.tagline}
                    </Text>
                    <Box pt="200" position="relative" style={{ maxWidth: '360px', overflow: 'hidden' }}>
                      <Image
                        src={emptyStateTexture}
                        position="absolute"
                        m="auto"
                        style={{ inset: 0, opacity: 0.25, pointerEvents: 'none', mixBlendMode: 'multiply' }}
                      />
                      <MarqueeSuggestions
                        onSuggestionClick={handleSuggestionClick}
                        shouldIncludeHumanSupport={supportChat.isHandoverAllowed}
                      />
                    </Box>
                  </MotionWelcomePanel>
                </Box>
              </Box>
              <Box className={chatStyles.scrollContentWrapper} flexShrink="0">
                <RecentChats />
              </Box>
            </>
          ) : (
            <>
              <Conversation ref={conversationRef} footer={<ConversationScrollButton />}>
                <Box display="flex" flexDirection="column" gap="150" className={chatStyles.scrollContentWrapper}>
                  {messages.map((message, index) => (
                    <Message
                      key={getMessageKey(message, index)}
                      message={message}
                      session={session}
                      isLastMessage={!isSending && index === messages.length - 1}
                      isStreaming={isReceiving && index === messages.length - 1}
                      isRecovering={status === 'recovering' && index === messages.length - 1}
                      isStopped={lastTurnStopped && index === messages.length - 1}
                      skillSlugs={skillSlugs}
                    />
                  ))}
                  {isSending || isReceiving ? (
                    <AnimateMessage>
                      <Box py="100">
                        <CyclingStatusText />
                      </Box>
                    </AnimateMessage>
                  ) : null}
                </Box>
              </Conversation>
              <Box className={chatStyles.scrollContentWrapper} flexShrink="0">
                <NextMessageSuggestions
                  state={suggestionState}
                  onSuggestionClick={handleSuggestionClick}
                  shouldIncludeHumanSupport={supportChat.isHandoverAllowed}
                />
              </Box>
            </>
          )}
        </>
      )}
    </Box>
  );
}

function AiAgentTitle() {
  return <>AI Agent</>;
}

export function SidebarChat() {
  const router = useRouter();
  const workspace = useCurrentWorkspace();
  const { track } = useAnalytics();
  const { handleToggle } = useCopilotSidebar();
  const safeNavigate = useSafeNavigate();
  const isAgentFullscreen = useIsAgentFullscreen();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const shouldReduceMotion = useReducedMotion();
  if (workspace == null) {
    return null;
  }

  const sidebarPanelInitial = entranceFadeUpInitial(shouldReduceMotion);

  return (
    <Sidebar
      ref={sidebarRef}
      id="copilot"
      onToggle={handleToggle}
      onToggleComplete={(isOpen) => {
        if (isOpen) {
          sidebarRef.current?.querySelector<HTMLElement>('#copilot-chat-input')?.focus();
        }
      }}
    >
      <Box display="flex" flexDirection="column" h="full" w="full">
        <m.div
          initial={sidebarPanelInitial}
          animate={ENTRANCE_FADE_UP_ANIMATE}
          transition={entranceStaggerTransition(0, shouldReduceMotion)}
        >
          <SidebarHeader
            title={
              <AiEnabledGuard fallback={AiAgentTitle}>
                <CopilotName />
              </AiEnabledGuard>
            }
            titleSuffix={
              <AiEnabledGuard>
                <AgentBetaLabel />
              </AiEnabledGuard>
            }
            leadingActions={
              <AiEnabledGuard>
                <CopilotActions />
              </AiEnabledGuard>
            }
            trailingActions={
              <AiEnabledGuard>
                <CopilotSupportButton />

                {!isAgentFullscreen ? (
                  <Tooltip content="Open full screen" isCustom={true}>
                    {(context) => (
                      <Button
                        variant="subtle"
                        size="sm"
                        icon="expand"
                        isIconOnly={true}
                        onClick={() => {
                          if (workspace != null) {
                            track('aiAssistant', 'expandedToFullScreen', {});
                            safeNavigate(
                              router.buildLocation({
                                to: '/workspaces/$workspaceId/agent',
                                params: { workspaceId: workspace.id },
                                search: { 'ai-from': buildAiFrom() },
                              }).href,
                            );
                          }
                        }}
                        ref={context.reference.setReference}
                        {...context.reference.getReferenceProps()}
                      />
                    )}
                  </Tooltip>
                ) : null}
              </AiEnabledGuard>
            }
            onClose={() => {
              toggle('copilot');
            }}
          />
        </m.div>
        <AiEnabledGuard fallback={AiNotEnabledFallback}>
          <MotionSidebarMessagesColumn
            display="flex"
            flexDirection="column"
            flexGrow="1"
            minH="0"
            w="full"
            initial={sidebarPanelInitial}
            animate={ENTRANCE_FADE_UP_ANIMATE}
            transition={entranceStaggerTransition(1, shouldReduceMotion)}
          >
            <CopilotMessages />
          </MotionSidebarMessagesColumn>
          <m.div
            initial={sidebarPanelInitial}
            animate={ENTRANCE_FADE_UP_ANIMATE}
            transition={entranceStaggerTransition(2, shouldReduceMotion)}
          >
            <Box p="100">
              <CopilotInput />
            </Box>
          </m.div>
        </AiEnabledGuard>
      </Box>
    </Sidebar>
  );
}
